import { defaultPrivacyLevel } from '@va/constants';
import { getPrivacySettingsSucceeded } from '@va/dashboard/actions/api';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { ModalFormWrapper } from '@va/ui/components/modals';
import { Paragraph, TextInput, fontWeights } from '@va/ui/design-system';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { object, string } from 'yup';
import { useUpdatePrivacySettings } from './privacy-level-apiClient';
import usePrivacyLevelOptionsContext from './usePrivacyLevelOptionsContext';

enum FormFieldsNames {
  resetText = 'resetText',
}
const RESET = 'RESET';
const ResetPrivacyLevelModal = ({ isModalOpen, onClose }: { isModalOpen: boolean; onClose: () => void }) => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const { showSuccessNotification, showErrorNotification } = useAddNotification();
  const { isLoading, data, error, execute, isSucceeded } = useUpdatePrivacySettings();
  const { handleReset } = usePrivacyLevelOptionsContext();

  const { values, errors, handleChange, setFieldValue, touched, handleSubmit, resetForm } = useFormik({
    initialValues: {
      [FormFieldsNames.resetText]: '',
    },
    validationSchema: object().shape({
      [FormFieldsNames.resetText]: string()
        .required(translate('form.errors.requiredField'))
        .matches(/^RESET$/, translate('form.errors.invalidResetString')),
    }),
    onSubmit: (values) => {
      execute(defaultPrivacyLevel);
    },
  });

  useEffect(() => {
    if (isSucceeded) {
      showSuccessNotification(2000, translate('all.generalSettings.resetData.success'));
      dispatch(getPrivacySettingsSucceeded({ data: data?.payload }));
      handleReset();
      onClose();
    }
    if (error) {
      showErrorNotification();
    }
  }, [
    isSucceeded,
    error,
    showErrorNotification,
    showSuccessNotification,
    onClose,
    dispatch,
    translate,
    data?.payload,
    handleReset,
  ]);

  return (
    <ModalFormWrapper
      title={translate('all.privacyCenter.privacyLevel.resetAllSettings.title')}
      isOpen={isModalOpen}
      onClose={() => {
        onClose();
        resetForm();
      }}
      onSubmit={handleSubmit}
      saveButtonProps={{
        text: isLoading ? translate('button.isLoading') : translate('button.resetAllSettings'),
        color: 'tertiary',
      }}
    >
      <div className='flex flex-col p-3 gap-3'>
        <Paragraph colorClassName='text-gray-devilSolid' weight={fontWeights.semibold}>
          {translate('all.privacyCenter.privacyLevel.resetAllSettings.subInfo')}
        </Paragraph>
        <TextInput
          label={translate('form.labels.resetConfirmationText', { reset: RESET })}
          placeholder={translate('form.placeholders.resetConfirmationText', { reset: RESET })}
          name={FormFieldsNames.resetText}
          required
          onChange={handleChange}
          clearField={() => {
            setFieldValue(FormFieldsNames.resetText, '');
          }}
          value={values.resetText}
          error={errors.resetText && touched.resetText ? translate(errors.resetText) : undefined}
          autoFocus
        />
      </div>
    </ModalFormWrapper>
  );
};

export default ResetPrivacyLevelModal;
